import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  isAdminTokenValid,
  clearAdminToken,
  ensureAdminAxiosInterceptor,
} from '../../utils/adminSession';
import '../../styles/admin.css';

ensureAdminAxiosInterceptor();

function authHeaders() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('adeeb-token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const NAV_ITEMS = [
  { to: '/admin',            label: 'Dashboard',  end: true,  icon: '📊' },
  { to: '/admin/books',      label: 'Books',                  icon: '📚' },
  { to: '/admin/discover',   label: 'Discover Quiz',          icon: '\u{1F4DD}' },
  { to: '/admin/categories', label: 'Categories',             icon: '🏷️' },
  { to: '/admin/genres',     label: 'Genres',                 icon: '🎭' },
  { to: '/admin/users',      label: 'Users',                  icon: '👥' },
  { to: '/admin/schools',    label: 'Schools',                icon: '🏫' },
  { to: '/admin/messages',   label: 'Messages',               icon: '✉️' },
  { to: '/admin/newsletters', label: 'New-release emails',    icon: '\u{1F4E8}' },
  { to: '/admin/social-links', label: 'Social links',         icon: '\u{1F517}' },
  { to: '/admin/ratings',    label: 'Ratings',                icon: '⭐' },
  { to: '/admin/billing',    label: 'Billing',                icon: '💷' },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [stage, setStage] = useState('checking'); // checking | ok | denied

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get('/api/auth/me', { headers: authHeaders(), timeout: 8000 });
        if (!alive) return;
        if (data?.user?.role !== 'admin') {
          setStage('denied');
          return;
        }
        // Role is fine — but we also need a valid step-up admin token.
        if (!isAdminTokenValid()) {
          navigate('/admin/login', { replace: true });
          return;
        }
        setMe(data.user);
        setStage('ok');
      } catch {
        if (alive) setStage('denied');
      }
    })();

    // If any admin API call comes back with ADMIN_STEP_UP_REQUIRED, the
    // interceptor clears the token and dispatches this event.
    const onStepUpNeeded = () => navigate('/admin/login', { replace: true });
    window.addEventListener('adeeb-admin-step-up-needed', onStepUpNeeded);
    return () => {
      alive = false;
      window.removeEventListener('adeeb-admin-step-up-needed', onStepUpNeeded);
    };
  }, [navigate]);

  const onAdminLogout = async () => {
    try { await axios.post('/api/auth/admin/logout'); } catch { return; }
    clearAdminToken();
    navigate('/admin/login', { replace: true });
  };

  if (stage === 'checking') {
    return (
      <div className="admin-fullpage">
        <div className="admin-loading">
          <div className="admin-spinner" />
          <p>Checking access…</p>
        </div>
      </div>
    );
  }

  if (stage === 'denied') {
    return (
      <div className="admin-fullpage">
        <div className="admin-denied">
          <h1>Admin only</h1>
          <p>You need an admin account to view this page.</p>
          <div className="admin-denied-actions">
            <Link to="/" className="admin-btn">Go home</Link>
            <button type="button" className="admin-btn admin-btn-ghost" onClick={() => navigate('/login')}>Sign in</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-wrapper">
      <aside className="admin-sidebar" aria-label="Admin navigation">
        <div className="admin-brand">
          <img className="admin-brand-image" src="/branding/deenlit-logo.png" alt="DeenLit" width="2172" height="724" />
          <span className="admin-brand-sub">Admin Studio</span>
        </div>
        <nav className="admin-nav">
          {NAV_ITEMS.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) => `admin-nav-link${isActive ? ' is-active' : ''}`}
            >
              <span className="admin-nav-icon" aria-hidden="true">{n.icon}</span>
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="admin-sidebar-foot">
          <div className="admin-me">
            <div className="admin-me-name">{me?.name || me?.email}</div>
            <div className="admin-me-role">admin</div>
          </div>
          <button type="button" className="admin-exit" title="Lock admin (sign out of admin)" onClick={onAdminLogout}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>
      </aside>
      <main className="admin-main">
        <Outlet context={{ me }} />
      </main>
    </div>
  );
}
